// luck-info.ts — client-side render of the creature Luck / Fortune panel, reading
// from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces options/tools/encounter_creatures/encounter_creature_info/
// creature_fortune_luck_info_inc.php. Two parts:
//   - Luck: current luck (encounter_creatures.luck) + a permission-gated Adjust
//     block. The Adjust button keeps the existing PHP write endpoint
//     (adjusts/adjust_luck_inc.php via queryDataInputs) — DB mutation stays server
//     side; after the write we refresh the sheet + re-fetch the caster cache and
//     re-render this panel from the fresh blob.
//   - Fortune: the fortune essence breakdown. Fortune IS an essence
//     (computed.creature.essences.fortune), so we reuse renderEssenceTable() — same
//     Default/background/feat/ability/inventory/Other/Override/Total layout as the
//     essence panel (zero rows hidden; Other/Override PC-only & read-only).

import { hasPlayerPermission } from '../shared/array-types';
import { renderEssenceTable } from './essence-breakdown';
import { setActiveInfoPanel } from './active-panel';
import type { CachedCaster } from './types';

function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function num(v: unknown): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : 0;
}

/** Total Fortune (the luck ceiling). The server caps luck at layer_value(caster,'fortune')
 *  (adjust_luck_inc.php), so read the SAME source — ftLayerValue('fortune') — to keep the slider's
 *  right end aligned with the server clamp (mirrors how mana uses ftLayerValue('spirit')). Falls back
 *  to the computed fortune essence total. Each may be a rich {total,…} object or a scalar slot. */
function fortuneTotal(caster: CachedCaster): number {
    const layer = ftLayerValue(caster, 'fortune');
    if (layer !== undefined && layer !== null) {
        return num(typeof layer === 'object' && !Array.isArray(layer) ? (layer as Record<string, unknown>).total : layer);
    }
    const c = caster.computed;
    const rich = c?.creature?.essences?.fortune ?? c?.essences?.fortune;
    if (rich === undefined || rich === null) return 0;
    return num(typeof rich === 'object' && !Array.isArray(rich) ? (rich as Record<string, unknown>).total : rich);
}

function renderLuckSection(caster: CachedCaster, casterId: string): string {
    const ecArr = (caster.encounter_creatures ?? {}) as Record<string, unknown>;
    const encounterCreatureId = String(unwrap(ecArr.encounter_creature_id) ?? '').trim();
    const luck = num(ecArr.luck);

    let html = `
    <table class="table border mb-3">
        <thead><tr><th scope="col" colspan="2" class="table-warning align-middle">${iconMap('luck')} Luck</th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">Current Luck:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-info">${luck}</span></h3></td></tr>
        </tbody>
    </table>`;

    if (hasPlayerPermission(caster) && encounterCreatureId !== '') {
        // Luck slider spans -1 → Fortune (the luck ceiling). It's NOT a 0..max meter like mana/vitality,
        // so the min is -1 (luck can sit negative). Widen the ends if the live luck is somehow outside the
        // nominal band so the current value is always reachable on the track.
        const fortune = fortuneTotal(caster);
        const sliderMin = Math.min(-1, luck);
        const sliderMax = Math.max(Math.max(fortune, luck), sliderMin);
        const sliderVal = Math.min(sliderMax, Math.max(sliderMin, luck));

        // Slider ⇄ delta two-way sync (vanilla JS, mirrors the mana / vitality panels). The slider sets an
        // ABSOLUTE target; the value POSTED is the DELTA (target − baseline luck), which adjust_luck_inc.php
        // expects. deltaOnInput clamps to the slider's own min/max (min can be negative here).
        const sliderOnInput = "(function(){var b=parseInt(document.getElementById('ec_luck_baseline').value,10);if(isNaN(b))b=0;var t=parseInt(document.getElementById('ec_luck_range').value,10);if(isNaN(t))t=0;var d=t-b;var h=document.getElementById('input_number_luck');if(h){h.value=d;}var lbl=document.getElementById('ec_luck_range_label');if(lbl){lbl.textContent=t;}})()";
        const deltaOnInput  = "(function(){var b=parseInt(document.getElementById('ec_luck_baseline').value,10);if(isNaN(b))b=0;var d=parseInt(document.getElementById('input_number_luck').value,10);if(isNaN(d))d=0;var r=document.getElementById('ec_luck_range');if(!r)return;var mn=parseInt(r.min,10);if(isNaN(mn))mn=0;var mx=parseInt(r.max,10);if(isNaN(mx))mx=0;var t=Math.min(mx,Math.max(mn,b+d));r.value=t;var lbl=document.getElementById('ec_luck_range_label');if(lbl){lbl.textContent=t;}})()";

        // Write stays server-side. adjust_luck_inc.php → process_affected_ids() broadcasts a
        // websocket caster_update, which refreshes window.FateTide.casterCache AND re-renders
        // this open panel via the caster-update.ts → ftRerenderOpenInfoPanel hook. No full PHP
        // side-panel re-query, no ping-back. refreshCasterSheet keeps the sheet bars immediate.
        // Bind the adjust to THIS panel's creature (cache-sourced encounterCreatureId), not the
        // ambient #caster_id global — standardized per-creature action path (ftAdjustForCaster).
        const handler =
            "(async()=>{try{"
            // The resource truth seam (websocket/resource-truth.ts ftResourceAdjust) — never a legacy post (N6 2026-10-01).
            + `var o=await window.ftResourceAdjust('luck','${encounterCreatureId}','input_number_luck');`
            // The truth lane renders from the committed row; the sheet re-query is the LEGACY path's refresh only.
            + `if(o!=='handled'&&o!=='refused')await refreshCasterSheet('${encounterCreatureId}');`
            + "}catch(e){console.error('Error updating luck:',e);}})()";

        html += `
        <div class="p-1 mt-2 mb-2 border rounded">
            <input type="hidden" id="ec_luck_baseline" value="${luck}">
            <div class="form-group mb-3">
                <h5 class="text-center mb-2">${iconMap('luck')} Set current luck</h5>
                <div class="d-flex align-items-center px-1">
                    <span class="small text-muted mr-2">${sliderMin}</span>
                    <input type="range" id="ec_luck_range" class="form-range flex-grow-1 mx-1" min="${sliderMin}" max="${sliderMax}" step="1" value="${sliderVal}" aria-label="Set current luck" oninput="${escapeHtml(sliderOnInput)}">
                    <span class="small text-muted ml-2">${iconMap('fortune')} ${sliderMax}</span>
                </div>
                <div class="text-center small text-muted" id="ec_luck_range_label">${sliderVal}</div>
                <p class="small text-muted text-center mb-0">Drag to your target current luck (−1 to Fortune), then tap Adjust Luck.</p>
            </div>
            <div class="form-group mb-3">
                <h5 class="text-center">Adjust (delta):</h5>
                <input id="input_number_luck" class="form-control text-center" type="number" value="0" oninput="${escapeHtml(deltaOnInput)}">
            </div>
            <div class="text-center">
                <button class="btn btn-primary" onclick="${escapeHtml(handler)}">${iconMap('luck')} Adjust Luck</button>
            </div>
        </div>`;
    }

    return html;
}

function renderLuckFortunePanel(caster: CachedCaster, casterId: string): string {
    return renderLuckSection(caster, casterId) + renderEssenceTable(caster, 'fortune');
}

/** Entry point. Repointed onclick: the Luck/Fortune box in build_v_mana_ecac_luck_bars_inc.php. */
export async function renderLuckFortuneInfoPanel(
    casterId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderLuckFortunePanel(c, casterId);
    setActiveInfoPanel(casterId, targetId, render);
    target.innerHTML = render(caster);
}
